import React, { useState, useEffect } from 'react';
import { Mail, MessageCircle, MapPin, Building2, Search, Sparkles, Users, User } from 'lucide-react';
import { formatPositionsDisplay } from '../services/positionAPIs';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  Fade,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';

function HRStaff() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [hrStaff, setHrStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileImages, setProfileImages] = useState({});

  useEffect(() => {
    const fetchHrStaff = async () => {
      try {
        setLoading(true);
        // Import your actual service functions
        const { getHrStaff, getEmployeeImage } = await import('../services/HRstaff');
        const data = await getHrStaff();

        // Transform the data to match expectations
        const transformedData = data.map(employee => ({
          id: employee.employeeId,
          name: employee.firstName || 'N/A',
          surname: employee.lastName || 'N/A',
          fullName: `${employee.firstName || 'N/A'} ${employee.lastName || 'N/A'}`,
          email: employee.user?.email || 'No email',
          role: formatPositionsDisplay(employee) || 'No role',
          department: employee.department?.name || 'No department',
          joiningDate: employee.dateOfJoining || 'N/A',
          position: formatPositionsDisplay(employee),
          dob: employee.dob || 'N/A',
          phone: employee.phone || 'N/A',
          office: employee.office?.name || 'No office',
          gender: employee.gender || 'N/A',
          cin: employee.cin || 'N/A',
          cnss: employee.cnss || 'N/A',
          diploma: employee.diploma || 'N/A',
          createdBy: 'HR Admin',
          status: employee.status?.toLowerCase() || 'active',
          hasImage: !!employee.image,
        }));
        setHrStaff(transformedData);

        // Fetch profile images for employees with images
        const imagePromises = transformedData
          .filter(emp => emp.hasImage)
          .map(async (emp) => {
            try {
              const imageUrl = await getEmployeeImage(emp.id);
              return { id: emp.id, imageUrl };
            } catch (err) {
              console.error(`Failed to load image for employee ${emp.id}:`, err);
              return { id: emp.id, imageUrl: null };
            }
          });

        const images = await Promise.all(imagePromises);
        const imageMap = images.reduce((acc, { id, imageUrl }) => {
          acc[id] = imageUrl;
          return acc;
        }, {});
        setProfileImages(imageMap);
      } catch (err) {
        setError('Failed to load HR staff. Please try again later.');
        console.error('Fetch HR staff error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHrStaff();

    return () => {
      Object.values(profileImages).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const filteredHRStaff = hrStaff.filter(staff =>
    staff.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Alert severity="error" sx={{ width: '100%', maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 5, px: 2 }}>
      {/* Header Section */}
      <Container maxWidth="xl" sx={{ mb: 6 }}>
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Sparkles size={32} color={theme.palette.primary.main} />
              <Typography
                variant="h3"
                component="h1"
                fontWeight="800"
                color="primary"
                sx={{
                  textShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                  letterSpacing: '-1px'
                }}
              >
                HR Staff Directory
              </Typography>
              <Sparkles size={32} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" color="text.secondary" fontWeight="400">
              Connect with our amazing team members
            </Typography>
          </Box>
        </Fade>

        {/* Search Input */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
          <TextField
            variant="outlined"
            placeholder="Search staff by name, email, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: '100%',
              maxWidth: 600,
              bgcolor: 'background.paper',
              borderRadius: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                boxShadow: theme.shadows[3]
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Container>

      {/* Staff Cards Grid */}
      <Container maxWidth="xl">
        {filteredHRStaff.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Users size={80} style={{ opacity: 0.5, marginBottom: 20 }} color={theme.palette.text.disabled} />
            <Typography variant="h5" color="text.primary" fontWeight="600" gutterBottom>
              No staff members found matching your search
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {filteredHRStaff.map((staff, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={staff.id}>
                <Fade in timeout={500 + (index * 100)}>
                  <Card
                    elevation={4}
                    sx={{
                      borderRadius: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[10]
                      },
                      background: theme.palette.mode === 'dark'
                        ? `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`
                        : `linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)`
                    }}
                  >
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Avatar
                        src={profileImages[staff.id] || undefined}
                        sx={{
                          width: 100,
                          height: 100,
                          border: `4px solid ${theme.palette.background.default}`,
                          boxShadow: theme.shadows[4],
                          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                          fontSize: '2.25rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {!profileImages[staff.id] && (staff.name.charAt(0) + (staff.surname?.charAt(0) || ''))}
                      </Avatar>
                    </Box>

                    <Typography variant="h5" fontWeight="700" color="text.primary" gutterBottom>
                      {staff.fullName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, wordBreak: 'break-all' }}>
                      {staff.email}
                    </Typography>

                    <Box sx={{ width: '100%', mb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          bgcolor: theme.palette.action.selected,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <MapPin size={18} color={theme.palette.primary.main} />
                        <Typography variant="body2" fontWeight="500">{staff.office}</Typography>
                      </Paper>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          bgcolor: theme.palette.action.selected,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <Building2 size={18} color={theme.palette.secondary.main} />
                        <Typography variant="body2" fontWeight="500">{staff.department}</Typography>
                      </Paper>
                    </Box>

                    <Box sx={{ mt: 'auto', width: '100%', display: 'flex', gap: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Mail size={18} />}
                        href={`mailto:${staff.email}`}
                        sx={{
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: theme.palette.primary.main,
                          '&:hover': { bgcolor: theme.palette.primary.dark }
                        }}
                      >
                        Email
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<MessageCircle size={18} />}
                        href={`https://teams.microsoft.com/l/chat/0/0?users=${staff.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: '#6264A7', // Teams color
                          '&:hover': { bgcolor: '#4e5087' }
                        }}
                      >
                        Teams
                      </Button>
                    </Box>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default HRStaff;
